import { lazy, Suspense, useEffect } from 'react'
import ContactModalProvider from './components/ContactModal.jsx'
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom'
import Layout from './components/Layout.jsx'
import ScrollToTop from './components/ScrollToTop.jsx'
import CmsMediaLayer from './cms/CmsMediaLayer.jsx'
import Home from './pages/Home.jsx'
import Services from './pages/Services.jsx'
import ServiceDetail from './pages/ServiceDetail.jsx'

// Service landing pages are code-split (lazy) so they don't bloat the initial bundle.
const WebDevelopment = lazy(() => import('./pages/WebDevelopment.jsx'))
const AppDevelopment = lazy(() => import('./pages/AppDevelopment.jsx'))

const CyberSecurity = lazy(() => import('./pages/CyberSecurity.jsx'))
const DroneDevelopment = lazy(() => import('./pages/DroneDevelopment.jsx'))
const IotDevelopment = lazy(() => import('./pages/IotDevelopment.jsx'))
const UiUxDesign = lazy(() => import('./pages/UiUxDesign.jsx'))
const SocialMediaManagement = lazy(() => import('./pages/SocialMediaManagement.jsx'))
const DigitalMarketing = lazy(() => import('./pages/DigitalMarketing.jsx'))
const HowWeWork = lazy(() => import('./pages/HowWeWork.jsx'))
const BusinessConsulting = lazy(() => import('./pages/BusinessConsulting.jsx'))
const AiAutomation = lazy(() => import('./pages/AiAutomation.jsx'))
const SoftwareDevelopment = lazy(() => import('./pages/SoftwareDevelopment.jsx'))
const Portal = lazy(() => import('./pages/Portal.jsx'))
/* The auth pages are the only thing that pulls in Firebase (150KB). Loading
   them lazily keeps that off the critical path for every visitor who is
   just reading the site. */
const Sigin = lazy(() => import('./pages/Sigin.jsx'))
const Signup = lazy(() => import('./pages/Signup.jsx'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword.jsx'))
const ResetPassword = lazy(() => import('./pages/ResetPassword.jsx'))
import Products from './pages/Products.jsx'
import Projects from './pages/Projects.jsx'
import ProjectShowcaseDetail from './pages/ProjectShowcaseDetail.jsx'
import ProductDetail from './pages/ProductDetail.jsx'
import About from './pages/About.jsx'
import Contact from './pages/Contact.jsx'
import Careers from './pages/Careers.jsx'
import JobDetail from './pages/JobDetail.jsx'
import NotFound from './pages/NotFound.jsx'
import Analytics from './components/Analytics.jsx'
import WhatsAppButton from './components/WhatsAppButton.jsx'
import ClientTabBar from './components/ClientTabBar.jsx'
import TermsConditions from './pages/TermsConditions.jsx'
import PrivacyPolicy from './pages/PrivacyPolicy.jsx'
import GdprCompliance from './pages/GdprCompliance.jsx'
import Faq from './pages/Faq.jsx'

/* The favicon, set once per navigation.

   This component used to set document.title as well, from a hand-written
   path -> title table. That table ran after React Helmet and overwrote it,
   so every page shipped a generic derived title ("Cyber Security ·
   Spectranox") instead of the one its <Seo> component had carefully
   written, and the description, canonical and Open Graph tags no longer
   agreed with the title beside them. The title is the strongest on-page
   signal a search result has, so <Seo> owns it now and this only keeps the
   icon. */
function FaviconManager() {
  const location = useLocation();

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = '/images/spectranox.webp';
  }, [location]);

  return null;
}

/* A dark placeholder while an auth chunk arrives, so the page does not
   flash white on a slow connection. */
function AuthPage({ children }) {
  return <Suspense fallback={<div className="min-h-screen bg-[#060818]" />}>{children}</Suspense>
}

/* Case-study links shared before the rename point at /projects/:id.
   Navigate cannot substitute a param, so carry it across by hand. */
function LegacyProjectRedirect() {
  const { id } = useParams()
  return <Navigate to={`/portfolio/${id}`} replace />
}

export default function App() {
  return (
    <ContactModalProvider>
      {/* Both live inside the router: each needs the current route, one to
          record it and the other to name it in the WhatsApp message. */}
      <Analytics />
      <WhatsAppButton />
      {/* Bottom tab bar for a signed-in client on a phone. Mounted here so it
          spans the marketing site and the portal from one place. */}
      <ClientTabBar />
      <ScrollToTop />
      <FaviconManager />
      {/* Applies the Static Page (CMS) image/video decisions to every page. */}
      <CmsMediaLayer />
      <Routes>
        <Route path="/login" element={<AuthPage><Sigin /></AuthPage>} />
        <Route path="/signup" element={<AuthPage><Signup /></AuthPage>} />
        <Route path="/forgot-password" element={<AuthPage><ForgotPassword /></AuthPage>} />
        <Route path="/reset-password" element={<AuthPage><ResetPassword /></AuthPage>} />

        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="services" element={<Services />} />
          <Route path="services/website-development" element={<WebDevelopment />} />
          <Route path="services/app-development" element={<AppDevelopment />} />
          <Route path="services/cyber-security" element={<CyberSecurity />} />
          <Route path="services/drone-development" element={<DroneDevelopment />} />
          <Route path="services/iot-development" element={<IotDevelopment />} />
          <Route path="services/ui-ux-design" element={<UiUxDesign />} />
          <Route path="services/social-media-management" element={<SocialMediaManagement />} />
          <Route path="services/digital-marketing" element={<DigitalMarketing />} />
          <Route path="how-we-work" element={<HowWeWork />} />
          <Route path="services/business-consulting" element={<BusinessConsulting />} />
          <Route path="services/ai-automation" element={<AiAutomation />} />
          <Route path="/services/software-development" element={<SoftwareDevelopment />} />
          <Route path="services/:slug" element={<ServiceDetail />} />
          <Route path="products" element={<Products />} />
          <Route path="products/:slug" element={<ProductDetail />} />
          <Route path="portfolio" element={<Projects />} />
          <Route path="portfolio/:id" element={<ProjectShowcaseDetail />} />
          {/* /projects was the old address for this page; keep it working. */}
          <Route path="projects" element={<Navigate to="/portfolio" replace />} />
          <Route path="projects/:id" element={<LegacyProjectRedirect />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="careers" element={<Careers />} />
          <Route path="careers/:slug" element={<JobDetail />} />
          <Route path="terms" element={<TermsConditions />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="refund" element={<Navigate to="/terms" replace />} />
          <Route path="gdpr" element={<GdprCompliance />} />
          <Route path="faqs" element={<Faq />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* Standalone full-screen pages — no shared Navbar/Footer. */}
        <Route
          path="portal/*"
          element={
            <Suspense fallback={<div className="min-h-screen bg-[#060818]" />}>
              <Portal />
            </Suspense>
          }
        />

      </Routes>
    </ContactModalProvider>
  )
}
